import { Box, Typography, Divider } from "@mui/material";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { useState } from "react";
import i18n from "i18next";
import { AavoIconButton } from "src/components/common/buttons/AavoIconButton.tsx";
import { faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";

/** A chosen item with an id of its own, as the same item can be chosen many times. */
interface SelectedItem<T> {
	id: string;
	item: T;
}

export interface SubConfigurationBulkSelectionViewProps<T> {
	/** The items that can still be chosen besides [selected]. */
	getAvailableItems: (selected: T[]) => T[];
	getItemKey: (item: T) => string | number;
	getItemLabel: (item: T) => string;
	closeDialog: () => void;
	onSubmit: (selected: T[]) => Promise<unknown>;
}

/** Chooses many sub-configurations to add at once, in order, the same one possibly many times. */
export function SubConfigurationBulkSelectionView<T>({
	getAvailableItems,
	getItemKey,
	getItemLabel,
	closeDialog,
	onSubmit,
}: SubConfigurationBulkSelectionViewProps<T>) {
	const [selectedItems, setSelectedItems] = useState<SelectedItem<T>[]>([]);

	const availableItems = getAvailableItems(selectedItems.map((selected) => selected.item));

	return (
		<VerticalBox sx={{ flex: 1 }}>
			<HorizontalBox sx={{ flex: 1, gap: 2 }}>
				<AvailableItemsList
					items={availableItems}
					getItemKey={getItemKey}
					getItemLabel={getItemLabel}
					onAddItem={handleAddItem}
				/>

				<Divider
					orientation="vertical"
					flexItem
					sx={{
						borderColor: "grey.300",
						mx: 1,
					}}
				/>

				<SelectedItemsList
					selectedItems={selectedItems}
					getItemLabel={getItemLabel}
					onRemoveItem={handleRemoveItem}
				/>
			</HorizontalBox>

			<ActionButtons selectedCount={selectedItems.length} onCancel={closeDialog} onSubmit={handleSubmit} />
		</VerticalBox>
	);

	function handleAddItem(item: T) {
		setSelectedItems((prev) => [...prev, { id: crypto.randomUUID(), item }]);
	}

	function handleRemoveItem(id: string) {
		setSelectedItems((prev) => prev.filter((selected) => selected.id !== id));
	}

	async function handleSubmit() {
		await onSubmit(selectedItems.map((selected) => selected.item));
	}
}

interface AvailableItemsListProps<T> {
	items: T[];
	getItemKey: (item: T) => string | number;
	getItemLabel: (item: T) => string;
	onAddItem: (item: T) => void;
}

function AvailableItemsList<T>({ items, getItemKey, getItemLabel, onAddItem }: AvailableItemsListProps<T>) {
	const [filterText, setFilterText] = useState("");

	const filteredItems = items.filter((item) => getItemLabel(item).toLowerCase().includes(filterText.toLowerCase()));

	return (
		<VerticalBox sx={{ flex: 1 }}>
			<Typography variant="h6" sx={{ mb: 2, color: "text.primary", fontWeight: 600 }}>
				{i18n.t("options")}
			</Typography>
			<AavoTextField
				label={i18n.t("search")}
				value={filterText}
				onChange={(value) => setFilterText(value)}
				sx={{ mb: 2 }}
				size="small"
			/>
			<Box
				sx={{
					flex: 1,
					overflowY: "auto",
					border: "1px solid",
					borderColor: "grey.300",
					borderRadius: 2,
					backgroundColor: "grey.50",
					"&:hover": {
						borderColor: "grey.400",
					},
				}}
			>
				{filteredItems.length === 0 ?
					<Box sx={{ p: 3, textAlign: "center" }}>
						<Typography variant="body2" color="text.secondary">
							{filterText ? i18n.t("no_results") : i18n.t("no_sub_configurations")}
						</Typography>
					</Box>
				:	filteredItems.map((item) => (
						<HorizontalBox
							key={getItemKey(item)}
							sx={{
								alignItems: "center",
								justifyContent: "space-between",
								padding: 2,
								margin: 1,
								borderRadius: 1,
								backgroundColor: "white",
								border: "1px solid",
								borderColor: "grey.200",
								transition: "all 0.2s ease-in-out",
								"&:hover": {
									borderColor: "primary.main",
									backgroundColor: "primary.50",
									transform: "translateY(-1px)",
									boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
								},
							}}
						>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{getItemLabel(item)}
							</Typography>
							<AavoIconButton
								size="small"
								icon={faPlus}
								onClick={() => onAddItem(item)}
								sx={{
									color: "primary.main",
									"&:hover": {
										backgroundColor: "primary.100",
										transform: "scale(1.1)",
									},
								}}
							/>
						</HorizontalBox>
					))
				}
			</Box>
		</VerticalBox>
	);
}

interface SelectedItemsListProps<T> {
	selectedItems: SelectedItem<T>[];
	getItemLabel: (item: T) => string;
	onRemoveItem: (id: string) => void;
}

function SelectedItemsList<T>({ selectedItems, getItemLabel, onRemoveItem }: SelectedItemsListProps<T>) {
	return (
		<VerticalBox sx={{ flex: 1 }}>
			<HorizontalBox sx={{ alignItems: "center", justifyContent: "space-between", mb: 2 }}>
				<Typography variant="h6" sx={{ color: "text.primary", fontWeight: 600 }}>
					{i18n.t("selected_sub_configurators")}
				</Typography>
				{selectedItems.length > 0 && (
					<Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
						{selectedItems.length}
					</Typography>
				)}
			</HorizontalBox>
			<Box
				sx={{
					flex: 1,
					overflowY: "auto",
					border: "1px solid",
					borderColor: "grey.300",
					borderRadius: 2,
					backgroundColor: "grey.50",
					minHeight: 0,
					"&:hover": {
						borderColor: "grey.400",
					},
				}}
			>
				{selectedItems.length === 0 ?
					<Box sx={{ p: 4, textAlign: "center" }}>
						<Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
							{i18n.t("no_sub_configurations")}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{i18n.t("select_or_add_sub_configuration")}
						</Typography>
					</Box>
				:	selectedItems.map((selectedItem, index) => (
						<HorizontalBox
							key={selectedItem.id}
							sx={{
								alignItems: "center",
								justifyContent: "space-between",
								padding: 2,
								margin: 1,
								borderRadius: 1,
								backgroundColor: "white",
								border: "1px solid",
								borderColor: "success.200",
								transition: "all 0.2s ease-in-out",
								"&:hover": {
									borderColor: "error.main",
									backgroundColor: "error.50",
									transform: "translateY(-1px)",
									boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
								},
							}}
						>
							<HorizontalBox sx={{ alignItems: "center", gap: 1 }}>
								<Typography
									variant="body2"
									sx={{
										backgroundColor: "success.100",
										color: "success.800",
										px: 1,
										py: 0.5,
										borderRadius: 1,
										fontSize: "0.75rem",
										fontWeight: 600,
									}}
								>
									{index + 1}
								</Typography>
								<Typography variant="body1" sx={{ fontWeight: 500 }}>
									{getItemLabel(selectedItem.item)}
								</Typography>
							</HorizontalBox>
							<AavoIconButton
								size="small"
								icon={faTrash}
								onClick={() => onRemoveItem(selectedItem.id)}
								sx={{
									color: "error.main",
									"&:hover": {
										backgroundColor: "error.100",
										transform: "scale(1.1)",
									},
								}}
							/>
						</HorizontalBox>
					))
				}
			</Box>
		</VerticalBox>
	);
}

interface ActionButtonsProps {
	selectedCount: number;
	onCancel: () => void;
	onSubmit: () => Promise<void>;
}

function ActionButtons({ selectedCount, onCancel, onSubmit }: ActionButtonsProps) {
	return (
		<HorizontalBox sx={{ mt: 3, justifyContent: "flex-end", gap: 2 }}>
			<AsyncButton variant="outlined" onClick={onCancel} color="secondary">
				{i18n.t("cancel")}
			</AsyncButton>
			<AsyncButton
				variant="contained"
				onClick={onSubmit}
				disabled={selectedCount === 0}
				sx={{
					minWidth: 120,
				}}
			>
				{selectedCount === 0 ? i18n.t("add") : `${i18n.t("add")} (${selectedCount})`}
			</AsyncButton>
		</HorizontalBox>
	);
}
